import { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Modal, ModalFooter } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Alert } from '../ui/Alert';
import { setup2FA, verify2FASetup } from '../../api/two-factor';
import { RecoveryCodesDisplay } from './RecoveryCodesDisplay';

interface TwoFactorSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type SetupStep = 'loading' | 'scan' | 'verify' | 'recovery';

export function TwoFactorSetupModal({ isOpen, onClose, onSuccess }: TwoFactorSetupModalProps) {
  const [step, setStep] = useState<SetupStep>('loading');
  const [secret, setSecret] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && step === 'loading') {
      startSetup();
    }
  }, [isOpen, step]);

  const startSetup = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await setup2FA();
      setSecret(response.secret);
      setQrCodeUrl(response.qrCodeUrl);
      setStep('scan');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start 2FA setup');
      setStep('scan');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const response = await verify2FASetup(verificationCode, secret);
      setRecoveryCodes(response.recoveryCodes);
      setStep('recovery');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleComplete = () => {
    setStep('loading');
    setSecret('');
    setQrCodeUrl('');
    setVerificationCode('');
    setRecoveryCodes([]);
    setError(null);
    onSuccess();
    onClose();
  };

  const handleClose = () => {
    if (step === 'recovery') {
      handleComplete();
    } else {
      setStep('loading');
      setSecret('');
      setQrCodeUrl('');
      setVerificationCode('');
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Set up two-factor authentication" size="md">
      {step === 'loading' && (
        <div className="flex flex-col items-center justify-center py-8 space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-surface-inverse" />
          <p className="text-body-sm text-text-tertiary">Setting up two-factor authentication...</p>
        </div>
      )}

      {step === 'scan' && (
        <div className="space-y-6">
          {error && (
            <Alert variant="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <p className="text-body-sm text-text-secondary">
            Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.)
          </p>

          {qrCodeUrl && (
            <div className="flex justify-center">
              <div className="p-4 bg-white rounded-lg">
                <QRCodeSVG value={qrCodeUrl} size={200} />
              </div>
            </div>
          )}

          {secret && (
            <div className="text-center">
              <p className="text-body-xs text-text-tertiary mb-2">
                Or enter this code manually:
              </p>
              <code className="text-body-sm bg-surface-secondary px-3 py-2 rounded font-mono select-all">
                {secret}
              </code>
            </div>
          )}

          <ModalFooter>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => setStep('verify')} disabled={!secret}>
              Continue
            </Button>
          </ModalFooter>
        </div>
      )}

      {step === 'verify' && (
        <form onSubmit={handleVerify} className="space-y-6">
          <p className="text-body-sm text-text-secondary">
            Enter the 6-digit code from your authenticator app to verify setup.
          </p>

          {error && (
            <Alert variant="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Input
            label="Verification code"
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
            placeholder="000000"
            autoFocus
            required
          />

          <ModalFooter>
            <Button type="button" variant="outline" onClick={() => setStep('scan')}>
              Back
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={verificationCode.length !== 6 || isLoading}
            >
              {isLoading ? 'Verifying...' : 'Verify'}
            </Button>
          </ModalFooter>
        </form>
      )}

      {step === 'recovery' && (
        <div className="space-y-6">
          <Alert variant="success">
            Two-factor authentication has been enabled successfully!
          </Alert>

          <RecoveryCodesDisplay codes={recoveryCodes} />

          <ModalFooter>
            <Button variant="primary" onClick={handleComplete}>
              Done
            </Button>
          </ModalFooter>
        </div>
      )}
    </Modal>
  );
}
